import React, { useEffect, useMemo, useState } from "react";
import { Globe, Activity, ShieldAlert, ArrowDown, ArrowUp, RotateCcw } from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust the import path as needed
import { showError } from "../../../utils/toast"; // Adjust the import path as needed
import AssetArpTable from "./AssetArpTable";

interface IPAddress {
    ip_address: string;
    netmask: string;
    interface_index: number;
}

interface ICMPStats {
    in_messages: number;
    out_messages: number;
    in_errors: number;
    out_errors: number;
}

interface NetworkProps {
    asset_id: string | number;
    client_id?: string | number;
}

export default function NetworkStats({ asset_id, client_id }: NetworkProps) {
    const [ipAddresses, setIpAddresses] = useState<IPAddress[]>([]);
    const [icmpStats, setIcmpStats] = useState<ICMPStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    useEffect(() => {
        if (asset_id) {
            fetchData();
        }
    }, [asset_id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: asset_id, client_id: client_id })
            };

            const response = await baseapi.get("/switch_details", { params });

            if (response.data.status && response.data.data?.length > 0) {
                const switchData = response.data.data[0];

                // Parse IP Addresses
                if (switchData.ip_addresses) {
                    try {
                        setIpAddresses(
                            typeof switchData.ip_addresses === "string"
                                ? JSON.parse(switchData.ip_addresses)
                                : switchData.ip_addresses
                        );
                    } catch (e) {
                        console.error("Failed to parse IP Addresses", e);
                        setIpAddresses([]);
                    }
                } else {
                    setIpAddresses([]);
                }

                // Parse ICMP Statistics
                if (switchData.icmp_statistics) {
                    try {
                        setIcmpStats(
                            typeof switchData.icmp_statistics === "string"
                                ? JSON.parse(switchData.icmp_statistics)
                                : switchData.icmp_statistics
                        );
                    } catch (e) {
                        console.error("Failed to parse ICMP Stats", e);
                        setIcmpStats(null);
                    }
                } else {
                    setIcmpStats(null);
                }
            } else {
                setIpAddresses([]);
                setIcmpStats(null);
                // Optional: Only show error if it's an actual failure, not just "no data"
                // showError(response.data.message || "Failed to load network details");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const sortedIpAddresses = useMemo(() => {
        if (!sortConfig) return ipAddresses;
        const { key, direction } = sortConfig;

        return [...ipAddresses].sort((a: any, b: any) => {
            let aValue = a[key];
            let bValue = b[key];

            if (aValue === undefined || aValue === null) aValue = '';
            if (bValue === undefined || bValue === null) bValue = '';

            if (key === 'interface_index') {
                return direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
            }

            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [ipAddresses, sortConfig]);

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={10} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    const StatCard = ({ title, value, icon, valueColor }: { title: string, value: number | string, icon: React.ReactNode, valueColor: string }) => (
        <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800 flex items-center justify-between shadow-inner">
            <div className="flex items-center gap-2">
                <div className="p-1.5 bg-slate-800 rounded-md border border-slate-700">
                    {icon}
                </div>
                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{title}</span>
            </div>
            <span className={`text-lg font-black ${valueColor} leading-none`}>
                {value !== undefined && value !== null ? value.toLocaleString() : "-"}
            </span>
        </div>
    );

    return (
        <div className="space-y-4 animate-fade-in">
            {/* Header */}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-slate-900/30 rounded-xl border border-slate-800 border-dashed">
                    <RotateCcw className="w-8 h-8 text-orange-500 animate-spin mb-3" />
                    <p className="text-slate-300 text-xs font-bold uppercase tracking-widest">Loading Network Details...</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {/* ICMP Statistics Normal View (Grid Cards) */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                            <Activity className="w-4 h-4 text-sky-500" />
                            <h3 className="text-xs font-black text-sky-500 uppercase tracking-widest">ICMP Statistics</h3>
                        </div>

                        {icmpStats ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <StatCard
                                    title="In Messages"
                                    value={icmpStats.in_messages}
                                    icon={<ArrowDown className="w-4 h-4 text-emerald-500" />}
                                    valueColor="text-emerald-500"
                                />
                                <StatCard
                                    title="Out Messages"
                                    value={icmpStats.out_messages}
                                    icon={<ArrowUp className="w-4 h-4 text-blue-500" />}
                                    valueColor="text-blue-500"
                                />
                                <StatCard
                                    title="In Errors"
                                    value={icmpStats.in_errors}
                                    icon={<ShieldAlert className="w-4 h-4 text-rose-500" />}
                                    valueColor={icmpStats.in_errors > 0 ? "text-rose-500" : "text-slate-300"}
                                />
                                <StatCard
                                    title="Out Errors"
                                    value={icmpStats.out_errors}
                                    icon={<ShieldAlert className="w-4 h-4 text-rose-500" />}
                                    valueColor={icmpStats.out_errors > 0 ? "text-rose-500" : "text-slate-300"}
                                />
                            </div>
                        ) : (
                            <div className="bg-slate-900/30 rounded-lg border border-slate-800 p-4 text-center">
                                <p className="text-slate-300 text-[10px] font-bold uppercase tracking-widest">No ICMP data available</p>
                            </div>
                        )}
                    </div>

                    {/* IP Addresses Compact Table */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                            <Globe className="w-4 h-4 text-orange-500" />
                            <h3 className="text-xs font-black text-orange-500 uppercase tracking-widest">Configured IP Addresses</h3>
                        </div>

                        <div className="bg-slate-900/50 rounded-xl border border-slate-800 shadow-xl overflow-hidden flex flex-col">
                            <div className="overflow-x-auto custom-scroll">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-900/80 border-b border-slate-800">
                                        <tr>
                                            <th className="px-4 py-2 w-1/3 text-[9px] font-black text-slate-300 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('ip_address')}>
                                                <div className="flex items-center gap-1">
                                                    IP Address
                                                    <SortIcon column="ip_address" />
                                                </div>
                                            </th>
                                            <th className="px-4 py-2 w-1/3 text-[9px] font-black text-slate-300 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('netmask')}>
                                                <div className="flex items-center gap-1">
                                                    Netmask
                                                    <SortIcon column="netmask" />
                                                </div>
                                            </th>
                                            <th className="px-4 py-2 w-1/3 text-[9px] font-black text-slate-300 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('interface_index')}>
                                                <div className="flex items-center gap-1">
                                                    Interface Index
                                                    <SortIcon column="interface_index" />
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/60 bg-slate-900/20">
                                        {sortedIpAddresses.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="py-8 text-center text-slate-600 font-bold uppercase tracking-widest text-[10px]">
                                                    No IP addresses configured
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedIpAddresses.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-slate-800/40 transition-colors group">
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1 bg-orange-500/10 rounded group-hover:bg-orange-500/20 transition-colors">
                                                                <Globe className="w-3 h-3 text-orange-500" />
                                                            </div>
                                                            <span className="text-slate-200 text-[11px] font-black font-mono tracking-tight">{item.ip_address}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        <span className="text-slate-200 text-[10px] font-mono tracking-tight">{item.netmask}</span>
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        <span className="inline-flex items-center justify-center px-2 py-0.5 bg-slate-800 rounded text-slate-300 text-[10px] font-bold border border-slate-700">
                                                            idx: {item.interface_index}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div>
                        <AssetArpTable client_id={client_id} asset_id={asset_id} />
                    </div>
                </div>
            )}
        </div>
    );
}
